import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Play, Square, Plus, Trash2, Clock, CalendarDays, RefreshCw, AlertTriangle } from 'lucide-react';
import { isMarketClosed } from '../utils/marketCalendar';

const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
  color: #fff;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  background: #1f1f1f;
  border: 1px solid #333;
  border-radius: 12px;
  padding: 20px;
`;

const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #a3a3a3;
`;

const InputRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Input = styled.input`
  background: #141414;
  border: 1px solid #333;
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 14px;
  flex: 1;

  &:focus {
    outline: none;
    border-color: #e50914;
  }
`;

const Button = styled.button`
  background: ${props => props.$primary ? '#e50914' : '#333'};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$primary ? '#f6121d' : '#444'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TargetList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
`;

const TargetItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #141414;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #333;
`;

const TargetInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const TargetSymbol = styled.span`
  font-size: 12px;
  color: #a3a3a3;
`;

const TargetName = styled.span`
  font-size: 14px;
  font-weight: 500;
`;

const TargetPeriods = styled.div`
  font-size: 11px;
  color: #666;
  margin-top: 4px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }
`;

const ResetIconButton = styled.button`
  background: transparent;
  border: none;
  color: #f59e0b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  title: "삭제 후 새로수집";

  &:hover {
    background: rgba(245, 158, 11, 0.1);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const LogContainer = styled.div`
  background: #000;
  border: 1px solid #333;
  border-radius: 8px;
  padding: 12px;
  font-family: monospace;
  font-size: 12px;
  color: #a3a3a3;
  height: 300px;
  overflow-y: auto;
  margin-top: 24px;
  white-space: pre-wrap;
`;

const LogEntry = styled.div`
  color: ${props => props.$type === 'error' ? '#ef4444' : props.$type === 'success' ? '#22c55e' : '#a3a3a3'};
  margin-bottom: 4px;
`;

const getBaseUrl = () => {
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isLocal ? 'http://localhost:5000' : window.location.origin;
};

export default function DataGeneratorPage() {
    const [targets, setTargets] = useState([]);
    const [newSymbol, setNewSymbol] = useState('');
    const [newSymbolName, setNewSymbolName] = useState('');
    const [newSymbolPeriods, setNewSymbolPeriods] = useState(['D', 'W', 'M', '60']); // Default

    const AVAILABLE_PERIODS = ['1', '5', '15', '60', '240', 'D', 'W', 'M'];

    const [intervalMinutes, setIntervalMinutes] = useState(5);
    const [startTime, setStartTime] = useState('00:00');
    const [endTime, setEndTime] = useState('23:59');

    const [isRunning, setIsRunning] = useState(false);
    const [statusText, setStatusText] = useState('대기 중');
    const [logs, setLogs] = useState([]);

    const timerRef = useRef(null);

    const addLog = (msg, type = 'info') => {
        const time = new Date().toLocaleTimeString();
        setLogs(prev => [...prev, { time, msg, type }].slice(-100)); // Keep last 100 logs
    };

    const fetchTargets = async () => {
        try {
            const res = await axios.get(`${getBaseUrl()}/api/batch/targets`);
            setTargets(res.data);
        } catch (e) {
            addLog(`목록 조회 실패: ${e.message}`, 'error');
        }
    };

    useEffect(() => {
        fetchTargets();
    }, []);

    const handleAddTarget = async (e) => {
        e.preventDefault();
        if (!newSymbol || !newSymbolName) return;
        try {
            await axios.post(`${getBaseUrl()}/api/batch/targets`, {
                symbol: newSymbol,
                name: newSymbolName,
                periods: newSymbolPeriods
            });
            addLog(`목록 추가됨: ${newSymbolName} (${newSymbol}) - ${newSymbolPeriods.join(', ')}`, 'success');
            setNewSymbol('');
            setNewSymbolName('');
            fetchTargets();
        } catch (e) {
            addLog(`목록 추가 실패: ${e.message}`, 'error');
        }
    };

    const handleDeleteTarget = async (symbol) => {
        try {
            await axios.delete(`${getBaseUrl()}/api/batch/targets/${symbol}`);
            addLog(`목록 삭제됨: ${symbol}`, 'info');
            fetchTargets();
        } catch (e) {
            addLog(`목록 삭제 실패: ${e.message}`, 'error');
        }
    };

    const [resettingSymbol, setResettingSymbol] = useState(null);

    const handleResetAndRecollect = async (symbol, name) => {
        if (!window.confirm(`[${name}] 기존 가격 데이터를 모두 삭제하고 새로 수집합니다.\n\n잠시 시간이 걸릴 수 있습니다. 계속하시겠습니까?`)) return;
        setResettingSymbol(symbol);
        try {
            addLog(`[⚠] ${name} (${symbol}) - 기존 데이터 삭제 중...`, 'info');
            const res = await axios.post(`${getBaseUrl()}/api/batch/targets/${symbol}/reset`);
            addLog(`[✅] ${name} 새로수집 완료: ${JSON.stringify(res.data.results)}`, 'success');
        } catch (e) {
            addLog(`[❌] ${name} 새로수집 실패: ${e.message}`, 'error');
        } finally {
            setResettingSymbol(null);
        }
    };

    const checkTimeWindow = () => {
        // Holiday/Weekend Check
        if (isMarketClosed()) return false;

        const now = new Date();
        const currentMins = now.getHours() * 60 + now.getMinutes();

        const [startH, startM] = startTime.split(':').map(Number);
        const startMins = startH * 60 + startM;

        const [endH, endM] = endTime.split(':').map(Number);
        const endMins = endH * 60 + endM;

        return currentMins >= startMins && currentMins <= endMins;
    };

    const runBatchCycle = async () => {
        if (isMarketClosed()) {
            setStatusText('휴장일 (Market Closed)');
            addLog('오늘은 휴장일입니다. 작업을 중단합니다.', 'info');
            return;
        }

        if (!checkTimeWindow()) {
            setStatusText(`작업 시간 아님 (${startTime} ~ ${endTime} 대기중)`);
            addLog(`작업 시간이 아닙니다. 대기합니다.`, 'info');
            return;
        }

        setStatusText('데이터 수집 진행중...');

        let currentTargets = targets;
        try {
            // Fetch latest targets just in case new ones were added via TechnicalPage
            const res = await axios.get(`${getBaseUrl()}/api/batch/targets`);
            setTargets(res.data);
            currentTargets = res.data;
        } catch (e) {
            addLog(`작업 전 목록 갱신 실패 (기존 목록으로 진행): ${e.message}`, 'error');
        }

        addLog(`배치 사이클 시작 (${currentTargets.length}개 종목)`, 'info');

        for (let target of currentTargets) {
            // In Phase 2 this will call the actual update engine endpoint
            try {
                const requestedPeriods = target.periods || ['D', 'W', 'M', '60'];
                addLog(`수집 중: ${target.name} (${target.symbol}) [${requestedPeriods.join(',')}]...`, 'info');
                const res = await axios.post(`${getBaseUrl()}/api/batch/collect`, {
                    symbol: target.symbol,
                    periods: requestedPeriods
                });
                addLog(`수집 완료: ${target.name} - ${JSON.stringify(res.data.results)}`, 'success');
            } catch (e) {
                addLog(`수집 오류 (${target.symbol}): ${e.message}`, 'error');
            }
        }

        addLog(`배치 사이클 완료. 다음 주기 대기...`, 'info');
        setStatusText(`대기 중 (${intervalMinutes}분 간격)`);
    };

    const toggleBatch = () => {
        if (isRunning) {
            clearInterval(timerRef.current);
            setIsRunning(false);
            setStatusText('대기 중');
            addLog('작업이 수동으로 중지되었습니다.', 'info');
        } else {
            if (targets.length === 0) {
                addLog('작업 대상 종목이 없습니다.', 'error');
                return;
            }
            setIsRunning(true);
            addLog('스케줄러 시작됨', 'info');
            // Run immediately first time
            runBatchCycle();
            // Then set interval
            timerRef.current = setInterval(() => {
                runBatchCycle();
            }, intervalMinutes * 60 * 1000);
        }
    };

    const handleTimeChange = (e, setter) => {
        let val = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
        if (val.length === 2 && !val.includes(':') && (e.nativeEvent && e.nativeEvent.inputType !== 'deleteContentBackward')) {
            val += ':';
        }
        setter(val);
    };

    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, []);

    return (
        <PageContainer>
            <Header>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <Title>분석차트 데이터 생성 관리자</Title>
                    {isMarketClosed() && (
                        <div style={{ 
                            display: 'flex', alignItems: 'center', gap: '6px',
                            color: '#ef4444', fontSize: '11px', fontWeight: 700, 
                            background: 'rgba(239, 68, 68, 0.1)', padding: '4px 10px', 
                            borderRadius: '20px', border: '1px solid rgba(239, 68, 68, 0.3)' 
                        }}>
                            <AlertTriangle size={12} />
                            MARKET CLOSED
                        </div>
                    )}
                </div>
                <span style={{ color: isRunning ? '#22c55e' : '#a3a3a3' }}>
                    상태: {statusText}
                </span>
            </Header>

            <PanelGrid>
                {/* Scheduler Panel */}
                <Panel>
                    <PanelTitle><Clock size={20} /> 스케줄러 설정</PanelTitle>

                    <FormGroup>
                        <Label>작업 간격 (분)</Label>
                        <Input
                            type="number"
                            value={intervalMinutes}
                            onChange={e => setIntervalMinutes(Number(e.target.value))}
                            min="1"
                            disabled={isRunning}
                        />
                    </FormGroup>

                    <InputRow>
                        <FormGroup style={{ flex: 1 }}>
                            <Label>시작 시간</Label>
                            <Input
                                type="text"
                                placeholder="HH:mm"
                                maxLength="5"
                                value={startTime}
                                onChange={e => handleTimeChange(e, setStartTime)}
                                disabled={isRunning}
                            />
                        </FormGroup>
                        <FormGroup style={{ flex: 1 }}>
                            <Label>종료 시간</Label>
                            <Input
                                type="text"
                                placeholder="HH:mm"
                                maxLength="5"
                                value={endTime}
                                onChange={e => handleTimeChange(e, setEndTime)}
                                disabled={isRunning}
                            />
                        </FormGroup>
                    </InputRow>

                    <Button
                        $primary={!isRunning}
                        onClick={toggleBatch}
                        style={{ width: '100%', marginTop: '16px', background: isRunning ? '#333' : '#e50914' }}
                    >
                        {isRunning ? <><Square size={16} /> 작업 중지</> : <><Play size={16} /> 작업 시작</>}
                    </Button>
                </Panel>

                {/* Target List Panel */}
                <Panel>
                    <PanelTitle><CalendarDays size={20} /> 가격 요청 종목 목록 ({targets.length})</PanelTitle>

                    <form onSubmit={handleAddTarget} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <Input
                                placeholder="종목명 (예: 삼성전자)"
                                value={newSymbolName}
                                onChange={e => setNewSymbolName(e.target.value)}
                            />
                            <Input
                                placeholder="종목코드 (예: 005930)"
                                value={newSymbol}
                                onChange={e => setNewSymbol(e.target.value)}
                                style={{ width: '120px', flex: 'none' }}
                            />
                            <Button $primary type="submit"><Plus size={16} /></Button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', background: '#141414', padding: '8px', borderRadius: '6px', border: '1px solid #333' }}>
                            <span style={{ fontSize: '12px', color: '#a3a3a3', width: '100%' }}>수집 주기 선택 (분봉/일/주/월)</span>
                            {AVAILABLE_PERIODS.map(p => (
                                <label key={p} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={newSymbolPeriods.includes(p)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setNewSymbolPeriods([...newSymbolPeriods, p]);
                                            } else {
                                                setNewSymbolPeriods(newSymbolPeriods.filter(sp => sp !== p));
                                            }
                                        }}
                                    />
                                    {p === 'D' ? '일' : p === 'W' ? '주' : p === 'M' ? '월' : `${p}분`}
                                </label>
                            ))}
                        </div>
                    </form>

                    <TargetList>
                        {targets.map(t => (
                            <TargetItem key={t.symbol}>
                                <TargetInfo>
                                    <TargetName>{t.name}</TargetName>
                                    <TargetSymbol>{t.symbol}</TargetSymbol>
                                    {t.periods && t.periods.length > 0 && (
                                        <TargetPeriods>[{t.periods.join(', ')}]</TargetPeriods>
                                    )}
                                </TargetInfo>
                                <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
                                    <ResetIconButton
                                        title="삭제 후 새로수집 (수정주가 적용)"
                                        onClick={() => handleResetAndRecollect(t.symbol, t.name)}
                                        disabled={resettingSymbol === t.symbol}
                                    >
                                        <RefreshCw size={16} style={{ animation: resettingSymbol === t.symbol ? 'spin 1s linear infinite' : 'none' }} />
                                    </ResetIconButton>
                                    <IconButton onClick={() => handleDeleteTarget(t.symbol)}>
                                        <Trash2 size={16} />
                                    </IconButton>
                                </div>
                            </TargetItem>
                        ))}
                    </TargetList>
                </Panel>
            </PanelGrid>

            <LogContainer>
                {logs.map((log, i) => (
                    <LogEntry key={i} $type={log.type}>
                        [{log.time}] {log.msg}
                    </LogEntry>
                ))}
            </LogContainer>

        </PageContainer>
    );
}
